import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Branding from './Branding';

// TappyLabs Learning Aquarium Branding
const TappyLabsBranding = {
  colors: {
    primary: {
      learningAquariumBlue: "#0EA5E9",
      confidenceFishGold: "#F59E0B", 
      deepOcean: "#0F172A",
      clarityWhite: "#FAFAFA",
    },
    secondary: {
      ecosystemGreen: "#10B981",
      crystalWaterBlue: "#E3F8FF",
      aquariumGlassClear: "#F0FDFF",
      livingCoral: "#FFE4E1",
    },
    gradients: {
      aquariumDepth: "linear-gradient(135deg, #0EA5E9 0%, #0F172A 100%)",
      confidenceGlow: "linear-gradient(135deg, #F59E0B 0%, #F97316 100%)",
      crystalClear: "linear-gradient(135deg, #FAFAFA 0%, #F1F5F9 100%)",
      ecosystemFlow: "linear-gradient(135deg, #10B981 0%, #0EA5E9 100%)",
    }
  },
  components: {
    glassCard: {
      background: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      borderRadius: "16px",
      boxShadow: "0 8px 32px rgba(15, 23, 42, 0.1)",
    }
  }
};

const WelcomeHelper = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [helperName, setHelperName] = useState('Helper');
  const [friendName, setFriendName] = useState('your friend');
  const [roomToken, setRoomToken] = useState(null);

  const sessionId = searchParams.get('session_id');
  const source = searchParams.get('source');
  const room_token = searchParams.get('room_token');

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        // Verify the payment session
        const response = await axios.get(`/api/payment/verify-session?session_id=${sessionId}`);
        
        if (response.data.success) {
          setPaymentVerified(true);
          // Extract helper context if available
          const metadata = response.data.metadata || {};
          if (metadata.helped_friend) {
            setFriendName(metadata.helped_friend);
          }
          if (metadata.room_token) {
            setRoomToken(metadata.room_token);
          }
        }

        // Also check URL param for room_token
        if (room_token) {
          setRoomToken(room_token);
        }
      } catch (error) {
        console.error('Payment verification failed:', error);
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [sessionId]);

  const handleAction = (action) => {
    switch (action) {
      case 'rejoin_session':
        if (roomToken) {
          window.location.href = `/friend-join?token=${roomToken}`;
        }
        break;
      case 'help_friend':
        navigate('/dashboard?tab=collaboration');
        break;
      case 'own_homework':
        navigate('/dashboard?tab=tutor');
        break;
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'invite_friends':
        navigate('/dashboard?tab=friends');
        break;
      default:
        navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: TappyLabsBranding.colors.gradients.aquariumDepth,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          color: TappyLabsBranding.colors.primary.clarityWhite,
          fontSize: '18px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔬</div>
          Setting up your Learning Aquarium...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'white',
      padding: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        padding: '48px 32px',
        textAlign: 'center'
      }}>

        {/* Success Header */}
        <div style={{
          fontSize: '48px',
          marginBottom: '24px'
        }}>
          🎉
        </div>

        <h1 style={{
          fontSize: '28px',
          fontWeight: '600',
          color: '#111',
          marginBottom: '12px'
        }}>
          Your account is ready!
        </h1>

        {paymentVerified && (
          <div style={{
            background: '#10B981',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '12px',
            marginBottom: '24px',
            fontSize: '15px',
            fontWeight: '500'
          }}>
            Payment successful - Unlimited access activated
          </div>
        )}

        <p style={{
          fontSize: '17px',
          color: '#666',
          marginBottom: '32px',
          lineHeight: '1.6'
        }}>
          You can now help friends stay focused anytime, and they can help you back.
        </p>

        {/* What You Unlocked */}
        <div style={{
          background: '#F9FAFB',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '32px',
          textAlign: 'left'
        }}>
          <div style={{
            fontSize: '15px',
            color: '#6B7280',
            marginBottom: '12px'
          }}>
            With unlimited access
          </div>
          <div style={{
            fontSize: '15px',
            color: '#111',
            lineHeight: '2'
          }}>
            Help friends stay focused<br/>
            They can help you back<br/>
            Study together anytime<br/>
            AI tutor for your homework too
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          marginBottom: '24px'
        }}>
          {roomToken && (
            <button
              onClick={() => handleAction('rejoin_session')}
              style={{
                background: '#007AFF',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '18px 20px',
                fontSize: '17px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(0, 122, 255, 0.2)'
              }}
            >
              Rejoin {friendName}'s Session
            </button>
          )}

          <button
            onClick={() => handleAction('dashboard')}
            style={{
              background: 'white',
              color: '#007AFF',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '16px 20px',
              fontSize: '17px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Go to Dashboard
          </button>
        </div>

        <div style={{
          fontSize: '14px',
          color: '#9CA3AF',
          textAlign: 'center'
        }}>
          Check your email for login details
        </div>
      </div>

    </div>
  );
};

export default WelcomeHelper;